import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { Mail, ArrowLeft, KeyRound, CheckCircle, AlertCircle } from 'lucide-react';
import { FadeIn, SlideIn } from '../components/animations';
import toast from 'react-hot-toast';

const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(false);
  const navigate = useNavigate();

  const handleBlur = () => {
    setTouched(true);
    if (!email.trim()) {
      setError('Email is required');
    } else if (!validateEmail(email)) {
      setError('Please enter a valid email');
    } else {
      setError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    
    if (!email.trim()) {
      setError('Email is required');
      toast.error('Email is required');
      return;
    }
    
    if (!validateEmail(email)) {
      setError('Please enter a valid email');
      toast.error('Please enter a valid email');
      return;
    }

    setError('');
    setLoading(true);

    try {
      await authAPI.forgotPassword({ email });
      setSuccess(true);
      setTimeout(() => navigate('/reset-password', { state: { email } }), 2000);
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || '';
      let errorMsg = 'Unable to send reset email. Please try again later.';
      if (message.includes('not found') || message.includes('does not exist')) {
        errorMsg = 'No account found with this email address';
      }
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-md w-full py-12">
          <FadeIn>
            <div className="text-center mb-8">
              <Link to="/" className="inline-flex items-center mb-6">
                <img src="/images/logo.png" alt="BSAF" className="h-16 w-auto mx-auto" />
              </Link>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Reset Password</h2>
              <p className="text-gray-600">Enter your email to receive reset instructions</p>
            </div>
          </FadeIn>

          <SlideIn direction="up" delay={0.1}>
            {success ? (
              <div className="text-center py-8">
                <FadeIn>
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Check Your Email</h3>
                  <p className="text-gray-600 mb-8">
                    We've sent password reset instructions to <br />
                    <strong className="text-gray-900">{email}</strong>
                  </p>
                  <Link
                    to="/login"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
                  >
                    <ArrowLeft className="w-5 h-5" />
                    Back to Login
                  </Link>
                </FadeIn>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && touched && (
                  <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded flex items-center justify-between">
                    <span>{error}</span>
                    <button onClick={() => { setError(''); setTouched(false); }} className="text-red-500 hover:text-red-700">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                  <div className="relative">
                    <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${touched && validateEmail(email) ? 'text-green-500' : 'text-gray-400'}`} />
                    <input
                      type="text"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onBlur={handleBlur}
                      className={`w-full pl-11 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${touched ? (validateEmail(email) ? 'border-green-500' : 'border-red-500') : 'border-gray-300'}`}
                      placeholder="you@example.com"
                    />
                    {touched && validateEmail(email) && (
                      <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                    )}
                  </div>
                  {touched && error && (
                    <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" /> {error}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <>
                      Send Reset Link
                      <KeyRound className="w-5 h-5" />
                    </>
                  )}
                </button>

                <div className="text-center">
                  <Link to="/login" className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium">
                    <ArrowLeft className="w-4 h-4" />
                    Back to Login
                  </Link>
                </div>
              </form>
            )}
          </SlideIn>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-blue-600 to-indigo-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute inset-0" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1557804506-669a67965ba0?w=1200)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.2 }}></div>
        
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <FadeIn delay={0.2}>
            <h2 className="text-4xl font-bold mb-6">Recover Your Account</h2>
            <p className="text-xl text-blue-100 mb-8 leading-relaxed">
              No worries! Enter your email address and we'll send you instructions to reset your password.
            </p>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">🔒</span>
                </div>
                <span className="text-lg">Secure password reset</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">✉️</span>
                </div>
                <span className="text-lg">Instant email delivery</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">⏱️</span>
                </div>
                <span className="text-lg">Quick and easy process</span>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
